/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationsGenerateWorkOSAdminPortalLink } from "../funcs/organizationsGenerateWorkOSAdminPortalLink.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GenerateWorkOSAdminPortalLinkResult } from "../models/components/generateworkosadminportallinkresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GenerateWorkOSAdminPortalLinkRequest,
  GenerateWorkOSAdminPortalLinkSecurity,
} from "../models/operations/generateworkosadminportallink.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type GenerateWorkOSAdminPortalLinkMutationVariables = {
  request: GenerateWorkOSAdminPortalLinkRequest;
  security?: GenerateWorkOSAdminPortalLinkSecurity | undefined;
  options?: RequestOptions;
};

export type GenerateWorkOSAdminPortalLinkMutationData =
  GenerateWorkOSAdminPortalLinkResult;

export type GenerateWorkOSAdminPortalLinkMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * generateWorkOSAdminPortalLink organizations
 *
 * @remarks
 * Generate a WorkOS Admin Portal link for the given intent (e.g. dsync, sso).
 */
export function useGenerateWorkOSAdminPortalLinkMutation(
  options?: MutationHookOptions<
    GenerateWorkOSAdminPortalLinkMutationData,
    GenerateWorkOSAdminPortalLinkMutationError,
    GenerateWorkOSAdminPortalLinkMutationVariables
  >,
): UseMutationResult<
  GenerateWorkOSAdminPortalLinkMutationData,
  GenerateWorkOSAdminPortalLinkMutationError,
  GenerateWorkOSAdminPortalLinkMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildGenerateWorkOSAdminPortalLinkMutation(client, options),
    ...options,
  });
}

export function mutationKeyGenerateWorkOSAdminPortalLink(): MutationKey {
  return ["@gram/client", "organizations", "generateWorkOSAdminPortalLink"];
}

export function buildGenerateWorkOSAdminPortalLinkMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: GenerateWorkOSAdminPortalLinkMutationVariables,
  ) => Promise<GenerateWorkOSAdminPortalLinkMutationData>;
} {
  return {
    mutationKey: mutationKeyGenerateWorkOSAdminPortalLink(),
    mutationFn: function generateWorkOSAdminPortalLinkMutationFn({
      request,
      security,
      options,
    }): Promise<GenerateWorkOSAdminPortalLinkMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationsGenerateWorkOSAdminPortalLink(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
